import { useCallback, useEffect, useRef, useState } from 'react';
import { createPlayer } from '../../services/warmupSampler';

/**
 * One sampler engine per surface, as React state.
 *
 * The engine is created once per mount and stopped on unmount. `ensureLoaded`
 * is what a Play handler calls: it invokes `engine.load()` SYNCHRONOUSLY
 * (which creates and resumes the AudioContext inside the tap — the iOS rule),
 * then awaits the sample decode. The first tap on a cold page therefore
 * downloads ~4 MB before sound; `loading` is true meanwhile so the button can
 * say so instead of looking dead.
 *
 * `onEnd` is a ref, not a prop to the engine, so the routine player can change
 * what "the exercise finished" means without re-creating the engine.
 */
const IDLE = { status: 'idle', exerciseId: null, tonic: null, tonicName: '', top: null, topName: '', phase: null, rep: 0, done: false, bpm: 80, guideLevel: 'full', meter: 4, accompaniment: 'simple', voice: 'auto', voiceEffective: null, voiceAvailable: null, voiceLabel: null, octaveShift: 0, countingIn: false, loaded: false };

export default function useWarmupPlayer({ instrument = 'piano' } = {}) {
	const engineRef = useRef(null);
	const onEndRef = useRef(null);
	const [state, setState] = useState(IDLE);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState('');

	if (!engineRef.current) {
		engineRef.current = createPlayer({
			instrument,
			onState: (s) => setState(s),
			onEnd: (id) => {
				if (onEndRef.current) onEndRef.current(id);
			},
		});
	}

	useEffect(() => {
		const engine = engineRef.current;
		return () => engine.stop();
	}, []);

	const ensureLoaded = useCallback(async () => {
		const engine = engineRef.current;
		try {
			setLoading(true);
			await engine.load();
			setLoadError('');
			return true;
		} catch (err) {
			console.error('Warmup instrument failed to load:', err);
			setLoadError(err && err.message ? err.message : 'The piano did not load');
			return false;
		} finally {
			setLoading(false);
		}
	}, []);

	const setOnEnd = useCallback((fn) => {
		onEndRef.current = fn;
	}, []);

	return { engine: engineRef.current, state, loading, loadError, ensureLoaded, setOnEnd };
}
